import {
  ThunkAction,
  RemoveRememberedAccountError,
  RemoveRememberedAccountSuccess,
} from '../types';

import { AnalyticsClient } from '../utilities/analytics/analytics-web-client';
import { captureErrorMessage } from '../utilities/analytics/error-reporting';
import { PAGE_ID } from '../pages/login/remembered-accounts/constants';

export type RemoveRememberedAccountAction =
  | { type: 'REMOVE_REMEMBERED_ACCOUNT_REQUEST_INITIATE'; payload: { accountIds: string[] } }
  | {
      type: 'REMOVE_REMEMBERED_ACCOUNT_REQUEST_SUCCESS';
      payload: { accountIds: string[]; response: RemoveRememberedAccountSuccess };
    }
  | {
      type: 'REMOVE_REMEMBERED_ACCOUNT_REQUEST_FAILED';
      payload: { response: RemoveRememberedAccountError };
    }
  | {
      type: 'RESET_REMEMBERED_ACCOUNT_RESPONSE_CODES';
    };

export const resetRememberMeResponseCodes = (): RemoveRememberedAccountAction => ({
  type: 'RESET_REMEMBERED_ACCOUNT_RESPONSE_CODES',
});

export const removeRememberedAccountInitiate = (
  accountIds: string[]
): RemoveRememberedAccountAction => ({
  type: 'REMOVE_REMEMBERED_ACCOUNT_REQUEST_INITIATE',
  payload: {
    accountIds,
  },
});

export const removeRememberedAccountSuccess = (
  accountIds: string[],
  success: RemoveRememberedAccountSuccess
): RemoveRememberedAccountAction => ({
  type: 'REMOVE_REMEMBERED_ACCOUNT_REQUEST_SUCCESS',
  payload: {
    accountIds,
    response: success,
  },
});

export const removeRememberedAccountFailure = (
  error: RemoveRememberedAccountError
): RemoveRememberedAccountAction => ({
  type: 'REMOVE_REMEMBERED_ACCOUNT_REQUEST_FAILED',
  payload: {
    response: error,
  },
});

/**
 * The request shape that we use to call the API with.
 */
export interface RemoveRememberedAccountRequest {
  accountIds: string[];
}

export type RemoveRememberedAccountResponse =
  | RemoveRememberedAccountResponseSuccess
  | RemoveRememberedAccountResponseFailure;

export interface RemoveRememberedAccountResponseSuccess {
  result: RemoveRememberedAccountSuccess;
}

export interface RemoveRememberedAccountResponseFailure {
  errorCode: RemoveRememberedAccountError;
}

export type RemoveRememberedAccountRequestParams = {
  accountIds: string[];
  analyticsClient: AnalyticsClient;
};

const ValidResponseValues: RemoveRememberedAccountSuccess[] = ['success', 'cookie_removed'];

/**
 * Helper function to type guard the response from the remove remembered account API.
 */
function isRemoveRememberedAccountSuccessResponse(
  response: RemoveRememberedAccountResponse
): response is RemoveRememberedAccountResponseSuccess {
  if ('result' in response) {
    return ValidResponseValues.includes(response.result as RemoveRememberedAccountSuccess);
  }

  return false;
}

const REMOVE_REMEMEMBERED_ACCOUNTS_ANALYTICS_ERRORS: Record<RemoveRememberedAccountError, string> =
  {
    bad_request: 'removeRememberedAccountBadRequest',
    invalid_cookie: 'removeRememberedAccountCookieInvalid',
    rate_limited: 'removeRememberedAccountRateLimited',
    unexpected_error: 'removeRememberedAccountUnexpectedError',
  };

export type RemoveRememberedAccountRequestSuccess = {
  result: RemoveRememberedAccountSuccess;
};

// Backend responds with `errorCode` when the request fails rather than `result` when it succeeds.
export type RemoveRememberedAccountRequestFailure = {
  errorCode: RemoveRememberedAccountError;
};

export const requestRemoveRememberedAccount =
  ({ accountIds, analyticsClient }: RemoveRememberedAccountRequestParams): ThunkAction =>
  (dispatch, getState) => {
    const { csrfToken } = getState();

    dispatch(removeRememberedAccountInitiate(accountIds));

    if (!accountIds || accountIds.length === 0) {
      dispatch(removeRememberedAccountFailure('bad_request'));
      return Promise.resolve();
    }

    const request: RemoveRememberedAccountRequest = {
      accountIds,
    };

    analyticsClient.trackingEvent({
      page: PAGE_ID,
      action: 'removeRememberedAccountInitiate',
      subject: 'account',
    });

    return fetch(`/rest/remember/remove`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
      body: JSON.stringify(request),
    })
      .then(response => response.json())
      .then((response: RemoveRememberedAccountResponse) => {
        // Successful case
        if (isRemoveRememberedAccountSuccessResponse(response)) {
          analyticsClient.trackingEvent({
            page: PAGE_ID,
            action: 'removeRememberedAccountSuccess',
            subject: 'account',
          });

          // Success, UI will update with the new values assuming state is updated.
          dispatch(removeRememberedAccountSuccess(accountIds, response.result));
          return;
        }

        const analyticsErrorType =
          REMOVE_REMEMEMBERED_ACCOUNTS_ANALYTICS_ERRORS[response?.errorCode] ??
          'removeRememberedAccountError';

        analyticsClient.trackingEvent({
          page: PAGE_ID,
          action: analyticsErrorType,
          subject: 'account',
        });

        // Handle errors, depending on the result
        dispatch(removeRememberedAccountFailure(response.errorCode));
      })
      .catch(error => {
        dispatch(removeRememberedAccountFailure('unexpected_error'));

        analyticsClient.trackingEvent({
          page: PAGE_ID,
          action: REMOVE_REMEMEMBERED_ACCOUNTS_ANALYTICS_ERRORS.unexpected_error,
          subject: 'account',
        });

        captureErrorMessage('Remove Remembered Account failure', error);
      });
  };
